/**
 * Path: packages/frontend/src/core/services/entityService.ts
 * Task: Offloads pure business logic (ID generation, entity creation, duplication, and datastore mutation) from the Editor store.
 * Update: Added support to generate explicit "Creation" history entries for new entities.
 */
import type { Entity, HistoryEntry, HistoryChange } from '@/core/types/editor';
import { getNextRank } from '@/store/utils/editorUtils';

export class EntityService {
  /**
   * Generates the next available sequence ID based on the project prefix and existing entities.
   */
  static generateNextEntityId(
    prefix: string,
    allEntities: Entity[],
    draftIds: (string | null | undefined)[] = []
  ): string {
    let maxSequence = 0;

    const examineIdForSequence = (id: string | null | undefined) => {
      if (id && id.startsWith(prefix)) {
        const numericalPart = id.substring(prefix.length);
        const sequenceNumber = parseInt(numericalPart, 10);
        if (!isNaN(sequenceNumber) && sequenceNumber > maxSequence) {
          maxSequence = sequenceNumber;
        }
      }
    };

    allEntities.forEach((entity) => examineIdForSequence(entity.id));
    draftIds.forEach(examineIdForSequence);

    return `${prefix}${maxSequence + 1}`;
  }

  /**
   * Creates a new, blank entity based on its global schema definition.
   */
  static createBlankEntity(
    entityType: string,
    newId: string,
    entitiesDef: any[],
    datastoreData: Record<string, Entity[]> | null
  ): Entity {
    const newEntity: any = {
      id: newId,
      title: 'New ' + (entityType.charAt(0).toUpperCase() + entityType.slice(1)),
      description: '',
      entityType: entityType,
      history: []
    };

    const entityDef = entitiesDef.find((e: any) => e.name === entityType);
    if (entityDef && entityDef.fields) {
      entityDef.fields.forEach((field: any) => {
        if (field.type === 'multiselect') {
          if (field.id.includes('.')) {
            const parts = field.id.split('.');
            if (!newEntity[parts[0]]) newEntity[parts[0]] = {};
            newEntity[parts[0]][parts[1]] = [];
          } else {
            newEntity[field.id] = [];
          }
        } else if (field.type === 'order') {
          newEntity[field.id] = getNextRank(datastoreData, entityType);
        } else if (field.type === 'select' && field.defaultValue) {
          newEntity[field.id] = field.defaultValue;
        }
      });
    }

    return newEntity as Entity;
  }

  /**
   * Safely duplicates an existing entity, clearing associative arrays and relations to prevent ghost-references.
   */
  static duplicateEntity(original: Entity, newId: string): Entity {
    const duplicated: Entity = JSON.parse(JSON.stringify(original));
    duplicated.id = newId;
    duplicated.title = `${original.title} (Copy)`;
    duplicated.lastUpdated = new Date().toISOString();

    for (const key in duplicated) {
      if (Array.isArray(duplicated[key])) {
        duplicated[key] = [];
      }
    }

    if (duplicated.raci) {
      for (const key in duplicated.raci) {
        if (Array.isArray(duplicated.raci[key])) {
          duplicated.raci[key] = [];
        }
      }
    }

    return duplicated;
  }

  /**
   * Compares the original and current entity state to generate a strict audit log entry.
   * If original is null, it generates a "Creation" entry.
   */
  static generateHistoryEntry(original: Entity | null, current: Entity, author: string): HistoryEntry | null {
    const changes: HistoryChange[] = [];

    // Handle Entity Creation
    if (!original) {
      changes.push({
        field: 'system.creation',
        oldValue: null,
        newValue: 'Entity created'
      });

      return {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
        datetime: new Date().toISOString(),
        author: author || 'Unknown',
        changes
      };
    }

    const ignoreKeys = new Set(['id', 'lastUpdated', 'updatedBy', 'history', 'comments', '_eType']);

    const normalizeVal = (v: any) => {
      if (Array.isArray(v) && v.length === 0) return null;
      if (v === '' || v === undefined) return null;
      return v;
    };

    const allKeys = new Set([...Object.keys(original), ...Object.keys(current)]);

    allKeys.forEach(key => {
      if (ignoreKeys.has(key) || key === 'raci') return;
      const oldVal = normalizeVal(original[key]);
      const newVal = normalizeVal(current[key]);

      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field: key,
          oldValue: oldVal,
          newValue: newVal
        });
      }
    });

    if (original.raci || current.raci) {
      const oldRaci = original.raci || {};
      const newRaci = current.raci || {};

      const raciKeys = new Set([...Object.keys(oldRaci), ...Object.keys(newRaci)]);
      raciKeys.forEach(rKey => {
        const oldVal = normalizeVal(oldRaci[rKey]);
        const newVal = normalizeVal(newRaci[rKey]);

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          changes.push({
            field: `raci.${rKey}`,
            oldValue: oldVal,
            newValue: newVal
          });
        }
      });
    }

    if (changes.length === 0) return null;

    return {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
      datetime: new Date().toISOString(),
      author: author || 'Unknown',
      changes
    };
  }

  /**
   * Generates the precise property key used on the source entity to store references to the target type.
   */
  static getRelationPropKey(systemKey: string, isMultiple: boolean): string {
    if (['cairo', 'predecessors'].includes(systemKey)) return systemKey;
    return isMultiple ? `${systemKey}Ids` : `${systemKey}Id`;
  }

  /**
   * Generates the precise property key used on the target entity to store references BACK to the source type.
   */
  static getInversePropKey(relationId: string, isMultiple: boolean = true): string {
    return isMultiple ? `inverse_${relationId}Ids` : `inverse_${relationId}Id`;
  }

  /**
   * Safely injects or updates a modified entity into the active local datastore structure.
   */
  static saveEntityToDatastore(entity: Entity, datastoreData: Record<string, Entity[]>, relationsDef: any[]): void {
    let list = datastoreData[entity.entityType];
    if (!list) {
      list = [];
      datastoreData[entity.entityType] = list;
    }

    const index = list.findIndex((e) => e.id === entity.id);
    if (index !== -1) {
      list[index] = JSON.parse(JSON.stringify(entity));
    } else {
      list.push(JSON.parse(JSON.stringify(entity)));
    }

    this.synchronizeRelationships(entity, datastoreData, relationsDef);
  }

  /**
   * Cleans up opposing ends of bidirectional relations dynamically based on app schemas
   * to ensure perfect symmetry in the data payload before sending to the backend.
   */
  static synchronizeRelationships(updatedEntity: Entity, datastoreData: Record<string, Entity[]>, relationsDef: any[]): void {
    relationsDef.forEach((rel: any) => {
      // CAIRO Matrix retains its own hardcoded sync logic below due to its complex sub-object structure.
      if (rel.id === 'cairo') return;

      const isMultiple = rel.isMultiple;
      // CRITICAL FIX: The source entity (Predecessor) holds its targets in 'successorIds'.
      // The target entity (Successor) holds its sources in 'predecessorIds'.
      const outProp = rel.id === 'predecessors' ? 'successorIds' : this.getRelationPropKey(rel.id, isMultiple);
      const inProp = rel.id === 'predecessors' ? 'predecessorIds' : this.getInversePropKey(rel.id, true);

      // --- 1. When updatedEntity acts as the SOURCE ---
      if (rel.sourceTypes?.includes(updatedEntity.entityType)) {
        const outgoingLinks: string[] = [];
        const rawVal = updatedEntity[outProp];
        if (Array.isArray(rawVal)) outgoingLinks.push(...rawVal);
        else if (rawVal) outgoingLinks.push(rawVal);

        const targetTypes = rel.targetTypes || [];
        targetTypes.forEach((tType: string) => {
          const targetList = datastoreData[tType] || [];
          targetList.forEach(otherEntity => {
            if (otherEntity.id === updatedEntity.id) return; // Prevent self-referencing loops if type points to itself

            if (outgoingLinks.includes(otherEntity.id)) {
              // Add to target's incoming links
              if (!otherEntity[inProp]) otherEntity[inProp] = [];
              if (!otherEntity[inProp].includes(updatedEntity.id)) {
                otherEntity[inProp].push(updatedEntity.id);
              }
            } else {
              // Remove from target's incoming links
              if (otherEntity[inProp]) {
                otherEntity[inProp] = otherEntity[inProp].filter((id: string) => id !== updatedEntity.id);
              }
            }
          });
        });
      }

      // --- 2. When updatedEntity acts as the TARGET ---
      if (rel.targetTypes?.includes(updatedEntity.entityType)) {
        const incomingLinks: string[] = [];
        const rawVal = updatedEntity[inProp];
        if (Array.isArray(rawVal)) incomingLinks.push(...rawVal);
        else if (rawVal) incomingLinks.push(rawVal);

        const sourceTypes = rel.sourceTypes || [];
        sourceTypes.forEach((sType: string) => {
          const sourceList = datastoreData[sType] || [];
          sourceList.forEach(sourceEntity => {
            if (sourceEntity.id === updatedEntity.id) return;

            if (incomingLinks.includes(sourceEntity.id)) {
              // Add to source's outgoing links
              if (isMultiple || rel.id === 'predecessors') {
                if (!sourceEntity[outProp]) sourceEntity[outProp] = [];
                if (!sourceEntity[outProp].includes(updatedEntity.id)) {
                  sourceEntity[outProp].push(updatedEntity.id);
                }
              } else {
                sourceEntity[outProp] = updatedEntity.id;
              }
            } else {
              // Remove from source's outgoing links
              if (isMultiple || rel.id === 'predecessors') {
                if (sourceEntity[outProp]) {
                  sourceEntity[outProp] = sourceEntity[outProp].filter((id: string) => id !== updatedEntity.id);
                }
              } else {
                if (sourceEntity[outProp] === updatedEntity.id) {
                  sourceEntity[outProp] = null;
                }
              }
            }
          });
        });
      }
    });

    // Isolated RACI Matrix Logic
    const raciDef = relationsDef.find((r: any) => r.id === 'cairo');
    if (raciDef) {
      const raciMap = [
        { art: 'accountableIds', role: 'accountableForartifactIds' },
        { art: 'responsibleIds', role: 'responsibleForartifactIds' },
        { art: 'consultedIds', role: 'consultedForartifactIds' },
        { art: 'informedIds', role: 'informedForartifactIds' },
        { art: 'omittedIds', role: 'omittedFromartifactIds' }
      ];

      const raciSourceTypes = raciDef.sourceTypes || [];
      const raciTargetTypes = raciDef.targetTypes || [];

      if (raciSourceTypes.includes(updatedEntity.entityType)) {
        const raci = updatedEntity.raci || {};
        raciTargetTypes.forEach((targetType: string) => {
          const roleList = datastoreData[targetType] || [];
          raciMap.forEach(({ art, role }) => {
            const assignedRoleIds = raci[art] || [];
            roleList.forEach(otherRole => {
              if (!otherRole.raci) otherRole.raci = {};
              if (assignedRoleIds.includes(otherRole.id)) {
                if (!otherRole.raci[role]) otherRole.raci[role] = [];
                if (!otherRole.raci[role].includes(updatedEntity.id)) otherRole.raci[role].push(updatedEntity.id);
              } else {
                if (otherRole.raci[role]) {
                  otherRole.raci[role] = otherRole.raci[role].filter((id: string) => id !== updatedEntity.id);
                }
              }
            });
          });
        });
      }
      else if (raciTargetTypes.includes(updatedEntity.entityType)) {
        const raci = updatedEntity.raci || {};
        raciSourceTypes.forEach((sourceType: string) => {
          const artifactsList = datastoreData[sourceType] || [];
          raciMap.forEach(({ art, role }) => {
            const assignedArtifactIds = raci[role] || [];
            artifactsList.forEach(otherArtifact => {
              if (!otherArtifact.raci) otherArtifact.raci = {};
              if (assignedArtifactIds.includes(otherArtifact.id)) {
                if (!otherArtifact.raci[art]) otherArtifact.raci[art] = [];
                if (!otherArtifact.raci[art].includes(updatedEntity.id)) otherArtifact.raci[art].push(updatedEntity.id);
              } else {
                if (otherArtifact.raci[art]) {
                  otherArtifact.raci[art] = otherArtifact.raci[art].filter((id: string) => id !== updatedEntity.id);
                }
              }
            });
          });
        });
      }
    }
  }

  /**
   * Drops an entity from the active local datastore structure and scrubs all references to it.
   */
  static deleteEntityFromDatastore(entityId: string, entityType: string, datastoreData: Record<string, Entity[]>): void {
    const list = datastoreData[entityType];
    if (list) {
      datastoreData[entityType] = list.filter((e) => e.id !== entityId);
    }

    Object.values(datastoreData).forEach((entityList) => {
      if (!Array.isArray(entityList)) return;

      entityList.forEach((entity) => {
        for (const key in entity) {
          if (key === 'history' || key === 'comments') continue;

          if (Array.isArray(entity[key])) {
            if (entity[key].length > 0 && typeof entity[key][0] === 'string') {
              entity[key] = entity[key].filter((val: string) => val !== entityId);
            }
          } else if (entity[key] === entityId) {
            entity[key] = null;
          }
        }

        if (entity.raci) {
          for (const raciKey in entity.raci) {
            if (Array.isArray(entity.raci[raciKey])) {
              entity.raci[raciKey] = entity.raci[raciKey].filter((val: string) => val !== entityId);
            }
          }
        }
      });
    });
  }
}
